import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { useRouter } from 'next/router';
import { NumberParam, StringParam, useQueryParams } from 'use-query-params';
import React, { useEffect, useRef, useState } from 'react';
import { useAppSelector } from 'redux/store';

import { SubsidyOverviewFieldsFragment } from 'api/graphql-types';
import { IActiveFilter, SubsidyAlgoliaFragment } from 'redux/stores/subsidy';

import { searchSubsidy } from 'api/algolia';

import useSubsidyFilters from './data/use-subsidy-filters';
import {
	GROUP_QUERY_KEY,
	GroupArrayParam,
	SCALE_QUERY_KEY,
	ScaleArrayParam,
	STATUS_QUERY_KEY,
	useGroup,
	useScale,
	useStatus
} from './queries';

import { SubsidyStatusFilter } from './tab-filter/tab-filter';
import MobileActions from './mobile-actions';

import FilterSidebar from 'components/filter-sidebar';
import { ITabItem } from 'components/tab-filter/tab-item/tab-item';
import { EmptyState } from 'components/empty-state/empty-state';
import { Search } from 'components/search/search';
import { ISortOption } from 'components/sort';
import Card from 'components/card';

import { useDebounce } from 'utils/use-debounce';

import styles from './subsidy-overview.module.scss';
import {
	THEME_QUERY_KEY,
	ThemeArrayParam,
	useTheme,
	KIND_QUERY_KEY,
	KindArrayParam,
	useKind,
	PAGE_QUERY_KEY,
	SORT_QUERY_KEY,
	NewOldSortParam,
	SEARCH_QUERY_KEY,
	useSort,
	useSearch,
	usePagination
} from 'utils/hooks/queries';

export const SUBSIDY_OVERVIEW_LIMIT = 15;

type FetchSubsidy = (
	page: number,
	newSort: ISortOption,
	newSearchValue: string | null,
	themeIds: string[],
	kindIds: string[],
	scaleIds: string[],
	groupIds: string[],
	newStatus: string | null | undefined
) => Promise<void>;

type SubsidyFetchProps = {
	page: number;
	sort: ISortOption;
	searchValue: string;
	themeIds: string[];
	kindIds: string[];
	scaleIds: string[];
	groupIds: string[];
	newStatus: string | null | undefined;
};

interface ISubsidyOverviewProps {
	module: SubsidyOverviewFieldsFragment;
}

const SubsidyOverview = (props: ISubsidyOverviewProps): React.JSX.Element => {
	const router = useRouter();
	const { formatMessage } = useIntl();

	const [query, setQuery] = useQueryParams({
		[PAGE_QUERY_KEY]: NumberParam,
		[SORT_QUERY_KEY]: NewOldSortParam,
		[SEARCH_QUERY_KEY]: StringParam,
		[THEME_QUERY_KEY]: ThemeArrayParam,
		[SCALE_QUERY_KEY]: ScaleArrayParam,
		[KIND_QUERY_KEY]: KindArrayParam,
		[GROUP_QUERY_KEY]: GroupArrayParam,
		[STATUS_QUERY_KEY]: StringParam
	});

	const overviewRef = useRef<HTMLDivElement>(null);
	const { subsidy, subsidyCount } = useAppSelector((state) => state.subsidy);
	const { kinds } = useAppSelector((state) => state.kinds);

	const [requests, setRequests] = useState<SubsidyAlgoliaFragment[]>(subsidy || []);
	const [requestIsLoading, setRequestIsLoading] = useState<boolean>(false);
	const [requestCount, setRequestCount] = useState<number>(subsidyCount);

	const [sortIsOpen, setSortIsOpen] = useState<boolean>(false);
	const [filtersIsOpen, setFiltersIsOpen] = useState<boolean>(false);
	const [initialQuery, setInitialQuery] = useState<typeof query | undefined>();
	const [hasFiltered, setHasFiltered] = useState<boolean>(false);

	const { currentPage, setCurrentPage, Pagination } = usePagination(SUBSIDY_OVERVIEW_LIMIT);
	const { sort, setSort, Sort } = useSort('subsidy', 'alfabetical');
	const { status, setStatus } = useStatus();
	const { searchValue, setSearchValue } = useSearch();
	const { selectedThemes, setSelectedThemesParam } = useTheme();
	const { selectedScales, setSelectedScalesParam } = useScale();
	const { selectedKinds, setSelectedKindsParam } = useKind(kinds);
	const { selectedGroups, setSelectedGroupsParam } = useGroup();

	const [search, setSearch] = useState<string | null>(searchValue);
	const debouncedSearch = useDebounce(search, 800);

	const fetchSubsidy: FetchSubsidy = async (
		page,
		newSort,
		newSearchValue,
		themeIds,
		kindIds,
		scaleIds,
		groupIds,
		newStatus
	): Promise<void> => {
		setRequestIsLoading(true);

		try {
			const { results, total } = await searchSubsidy(
				newSearchValue || '',
				{
					status: newStatus || 'open',
					subsidyKindsIds: kindIds,
					subsidyScalesIds: scaleIds,
					targetGroupsIds: groupIds,
					themesIds: themeIds
				},
				{
					sort: newSort.id === 'desc' ? 'desc' : 'asc',
					page,
					limit: SUBSIDY_OVERVIEW_LIMIT,
					locale: router.locale
				}
			);

			setRequests(results);
			setRequestCount(total);
		} catch {
			setRequests([]);
			setRequestCount(0);
		}

		setRequestIsLoading(false);
	};

	const scrollToTop = (): void => {
		if (typeof window !== 'undefined') {
			const extraOffset = 140;
			const moduleOffset = (overviewRef.current?.getBoundingClientRect().top || 0) + window.scrollY - extraOffset;
			window.scrollTo({ top: moduleOffset, left: 0, behavior: 'smooth' });
		}
	};

	const handleDeleteFilter = (item?: IActiveFilter): void => {
		if (item?.label) {
			const newSelectedThemes = selectedThemes.filter((selection) => selection.label !== item.label);
			setSelectedThemesParam(newSelectedThemes.length > 0 ? newSelectedThemes : null);

			if (item.label === selectedScales[0]?.label) {
				setSelectedScalesParam(null);
			}
			if (item.label === selectedKinds[0]?.label) {
				setSelectedKindsParam(null);
			}
			if (item.label === selectedGroups[0]?.label) {
				setSelectedGroupsParam(null);
			}

			handleFetchSubsidy({
				page: 1,
				themeIds: newSelectedThemes.map((newItem) => String(newItem.id)),
				scaleIds: item.label === selectedScales[0]?.label ? [] : selectedScales.map((scale) => String(scale.id)),
				kindIds: item.label === selectedKinds[0]?.label ? [] : selectedKinds.map((kind) => String(kind.id)),
				groupIds: item.label === selectedGroups[0]?.label ? [] : selectedGroups.map((group) => String(group.id))
			});
		} else {
			setQuery({
				thema: null,
				schaal: null,
				pagina: null,
				soort: null,
				doelgroep: null,
				sort: null,
				zoekterm: null,
				status: null
			});
			setSearch('');
			handleFetchSubsidy(undefined, true);
		}
	};

	const handleSortOnChange = (selectedSortFilter: { id: string; label: string } | undefined): void => {
		if (selectedSortFilter) {
			setSort(selectedSortFilter);
			handleFetchSubsidy({ page: 1, sort: selectedSortFilter });

			if (currentPage !== 1) {
				setCurrentPage(1);
			}
		}
	};

	const handleSearchOnChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
		setSearch(event.target.value);

		if (currentPage !== 1) {
			setCurrentPage(1);
		}
	};

	const handlePageChange = (page: number): void => {
		if (page !== currentPage) {
			setCurrentPage(page);
			handleFetchSubsidy({ page });
		}
	};

	const handleFetchSubsidy = async (data?: Partial<SubsidyFetchProps>, reset?: boolean): Promise<void> => {
		const selectedPage = reset ? 1 : data?.page || currentPage;
		const selectedSort = reset ? { id: '', label: '' } : data?.sort || sort;
		const selectedSearch = reset ? '' : data?.searchValue || debouncedSearch;
		const selectedStatus = reset ? 'open' : data?.newStatus || status;

		const selectedThemeIds = reset ? [] : data?.themeIds || selectedThemes.map((theme) => String(theme.id));
		const selectedKindIds = reset ? [] : data?.kindIds || selectedKinds.map((kind) => String(kind.id));
		const selectedScaleIds = reset ? [] : data?.scaleIds || selectedScales.map((scale) => String(scale.id));
		const selectedGroupIds = reset ? [] : data?.groupIds || selectedGroups.map((group) => String(group.id));

		scrollToTop();
		setHasFiltered(data !== undefined);
		fetchSubsidy(
			selectedPage,
			selectedSort,
			selectedSearch,
			selectedThemeIds,
			selectedKindIds,
			selectedScaleIds,
			selectedGroupIds,
			selectedStatus
		);
	};

	const subsidyFilters = useSubsidyFilters(
		props.module,
		(themeIds: string[], kindIds: string[], scaleIds: string[], groupIds: string[]) =>
			handleFetchSubsidy({ page: 1, themeIds, kindIds, scaleIds, groupIds })
	);

	useEffect(() => {
		handleFetchSubsidy(undefined);
	}, [initialQuery]);

	useEffect(() => {
		setSearchValue(debouncedSearch || null);

		if (debouncedSearch !== null) {
			handleFetchSubsidy({ page: 1 });
		}
	}, [debouncedSearch]);

	useEffect(() => {
		if (
			!hasFiltered &&
			!initialQuery &&
			(query[THEME_QUERY_KEY] ||
				query[SCALE_QUERY_KEY] ||
				query[KIND_QUERY_KEY] ||
				query[GROUP_QUERY_KEY] ||
				query[SEARCH_QUERY_KEY] ||
				query[STATUS_QUERY_KEY] ||
				query[SORT_QUERY_KEY])
		) {
			setInitialQuery(query);
		}
	}, [query]);

	useEffect(() => {
		if (!!query[SEARCH_QUERY_KEY] && initialQuery?.[SEARCH_QUERY_KEY] !== query[SEARCH_QUERY_KEY]) {
			setSearch(query[SEARCH_QUERY_KEY]);
		}
	}, [query[SEARCH_QUERY_KEY]]);

	const onStatusTabChange = (item: ITabItem): void => {
		setStatus(item.id as string);
		handleFetchSubsidy({ page: 1, newStatus: item.id });
	};

	return (
		<>
			<div className={styles.header}>
				<div className="container">
					<h2 className={styles.headerText}>{props.module.headerTitle}</h2>
					<Search
						searchValue={search || ''}
						searchPlaceholderText={props.module.searchPlaceholderText}
						handleSearchOnChange={handleSearchOnChange}
						small
					/>
					<SubsidyStatusFilter status={status || 'open'} onStatusTabChange={onStatusTabChange} />
				</div>
			</div>

			<div className={styles.overview} ref={overviewRef}>
				<div className="container">
					<div className={styles.overviewContainer}>
						<MobileActions
							filtersIsOpen={filtersIsOpen}
							setFiltersIsOpen={setFiltersIsOpen}
							sortIsOpen={sortIsOpen}
							setSortIsOpen={setSortIsOpen}
						/>

						<FilterSidebar
							module={props.module}
							mobileIsOpen={filtersIsOpen}
							setMobileIsOpen={setFiltersIsOpen}
							filters={subsidyFilters}
							handleDeleteFilters={handleDeleteFilter}
							handleResetFilter={handleDeleteFilter}
							amountOfActiveFilters={
								[...selectedThemes, ...selectedScales, ...selectedKinds, ...selectedGroups].length > 0 ? 1 : 0
							}
							activeFilters={
								[...selectedThemes, ...selectedScales, ...selectedKinds, ...selectedGroups] as IActiveFilter[]
							}
						/>

						<div className={styles.content}>
							<div className={styles.resultsHeader}>
								<h3 id={props.module.overviewTitleText}>{props.module.overviewTitleText}</h3>
								<div className={styles.sortBar}>
									<span id="pageResults" aria-live="polite">
										{formatMessage({ id: 'global.results' }, { results: requestCount })}
									</span>
									<Sort onChange={handleSortOnChange} mobileIsOpen={sortIsOpen} setMobileIsOpen={setSortIsOpen} />
								</div>
							</div>
							<div className={clsx(styles.results, requestIsLoading && styles.resultsLoading)}>
								{requests.length > 0 ? (
									requests?.map((s, index) => <Card key={`subsidy-${index}`} title={s?.title || ''} slug={s.slug} />)
								) : (
									<EmptyState onClearFilters={handleDeleteFilter} isLoading={requestIsLoading} />
								)}
							</div>
						</div>

						<div className={styles.pagination}>
							<Pagination totalCount={requestCount} onPageChange={handlePageChange} />
						</div>
					</div>
				</div>
			</div>
		</>
	);
};

export default SubsidyOverview;
